import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environment';
import { Cookie } from 'ng2-cookies/cookie';
import { of, Observable } from 'rxjs';
import { mergeMap, concatMap, toArray, shareReplay, map } from 'rxjs/operators';
import { IncidentSearchResult } from '../interface/incident.search.result';
import { DownloadService } from '../../modal/download-modal/download-modal.component';

@Injectable({
  providedIn: 'root',
})
export class IncidentService implements DownloadService {
  constructor(private http: HttpClient) {}

  getUserProfile() {
    return this.http.get<any>(
      `${environment.API_UMBRELLA_URL}/rms/v1/profile`,
      this.setHeader({ fetchWidget: true, api_key: environment.API_UMBRELLA_KEY }),
    );
  }

  searchIncidents(params: HttpParams) {
    params = Object.assign({}, params, { api_key: environment.API_UMBRELLA_KEY });
    return this.http.get<IncidentSearchResult>(
      `${environment.API_UMBRELLA_URL}/helpdesk/search/`,
      this.setHeader(params),
    );
  }

  getFollowingIncidents() {
    const params = { api_key: environment.API_UMBRELLA_KEY, domain: environment.CUSTOMER_SERVICE_DOMAIN_ID };
    return this.http.get<any>(
      `${environment.API_UMBRELLA_URL}/watchlistservice/v1/api/subscriptions`,
      this.setHeader(params),
    );
  }

  followIncident(incident) {
    const params = { api_key: environment.API_UMBRELLA_KEY };
    const body = this.getFollowUnfollowBody(incident, true);
    return this.http.post(
      `${environment.API_UMBRELLA_URL}/watchlistservice/v1/api/recorddetail/`,
      body,
      this.setHeader(params),
    );
  }

  unFollowIncident(incident, recordId) {
    const params = { api_key: environment.API_UMBRELLA_KEY };
    const body = this.getFollowUnfollowBody(incident, false);
    return this.http.put(
      `${environment.API_UMBRELLA_URL}/watchlistservice/v1/api/recorddetail/${recordId}`,
      body,
      this.setHeader(params),
    );
  }

  /**
   *
   * @param orgIds user selected orgs
   * @returns
   */
  getAgencyPickerData(orgIdss: string[]) {
    return of(orgIdss).pipe(
      mergeMap((orgIds) => orgIds),
      concatMap((orgId) => {
        return this.getOrgDetails(orgId).pipe(
          map((data) => {
            const { orgKey, l1Name, cgac } = data?.['_embedded']?.[0]?.org;
            return {
              orgKey: orgKey,
              orgText: `${cgac} - ${l1Name}`,
              levelText: 'Dept / Ind. Agency',
              highlighted: true,
            };
          }),
        );
      }),
      toArray(),
    );
  }

  /**
   *
   * @param orgId
   * @returns
   */
  getOrgDetails(orgId: string): Observable<any> {
    return this.http
      .get<any>(
        `${environment.API_UMBRELLA_URL}/federalorganizations/v1/organizations/${orgId}`,
        this.setHeader({
          api_key: environment.API_UMBRELLA_KEY,
        }),
      )
      .pipe(shareReplay(1));
  }

  private setHeader(params) {
    return {
      headers: {
        'X-Auth-Token': Cookie.get('SESSION'),
      },
      params: params,
    };
  }

  private getFollowUnfollowBody(incident, isActive) {
    const body = {
      id: 0,
      domainId: environment.CUSTOMER_SERVICE_DOMAIN_ID,
      type: 'FSD',
      recordId: incident.ticketId,
      frequency: 'daily',
      active: isActive ? 'Y' : 'N',
      uri: environment.ENTITY_DOCUMENT_REVIEW_URL + incident.ticketId,
      title: incident.helpDeskTicket,
      interestedVendor: true,
      orgRecordId: incident.helpDeskTicket,
    };
    return body;
  }

  downloadFile(format: string, fileName: string, url: string, queryParams: any): Observable<Blob> {
    if (!url) {
      url = `${environment.API_UMBRELLA_URL}/ui-export-service/v2/export-auth-download`;
    }
    queryParams.fileName = fileName;
    const params = {
      domain: 'escalation',
      format,
      parameters: JSON.stringify(queryParams),
    };
    const options = {
      headers: {
        'X-Auth-Token': Cookie.get('SESSION'),
      },
      responseType: 'blob' as 'json',
      params,
    };
    return this.http.get<Blob>(url, options);
  }
}
